import React, { useState, useEffect, useRef } from "react";
import styles from "./CustomInput.module.scss";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import DOMPurify from 'dompurify';

interface CustomInputProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  size?: "full" | "half" | "third";
  type?: "text" | "email" | "number" | "tel" | "password" | "ssn" | "ein" | "select";
  options?: { value: string; label: string }[];
}

const CustomInput: React.FC<CustomInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error = "",
  disabled = false,
  size = "full",
  type = "text",
  options = []
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [internalError, setInternalError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  const sanitizeInput = (value: string) => {
    return DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: {},
    });
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const formatPhone = (val: string) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 10);
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  };

  const formatSSN = (val: string) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 9);
    if (cleaned.length > 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
    if (cleaned.length > 3) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return cleaned;
  };

  const formatEIN = (val: string) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 9);
    if (cleaned.length > 2) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
    return cleaned;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let val = e.target.value;
    if (type === "tel") {
      val = formatPhone(val);
    } else if (type === "ssn") {
      val = formatSSN(val);
    } else if (type === "ein") {
      val = formatEIN(val);
    } else if (type === "number") {
      val = val.replace(/[^0-9]/g, "");
    } else {
      val = sanitizeInput(val);
    }
    setInternalError("");
    onChange(val);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (required && !value.trim()) {
      setInternalError("This field is required");
    } else if (type === "email" && value && !validateEmail(value)) {
      setInternalError("Please enter a valid email address");
    } else {
      setInternalError("");
    }
  };

  const handlePasswordToggle = () => setShowPassword((prev) => !prev);

  return (
    <div
      className={`${styles.inputWrapper} ${styles[size]} ${isFocused ? styles.focused : ""} ${internalError || error ? styles.error : ""}`}
    >
      <label
        className={`${styles.floatingLabel} ${value.trim() || isFocused ? styles.filled : ""}`}
      >
        {label} {required && "*"}
      </label>
      {type === "select" ? (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          disabled={disabled}
          className={styles.input}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type={type === "password" ? (showPassword ? "text" : "password") : type}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          placeholder={placeholder ?? ""}
          disabled={disabled}
          className={styles.input}
        />
      )}
      {type === "password" && (
        <button
          type="button"
          onClick={handlePasswordToggle}
          className={styles.togglePasswordButton}
          tabIndex={-1}
        >
          {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
        </button>
      )}
      <div
        className={`${styles.errorMessage} ${!internalError && !error ? styles.empty : ""}`}
      >
        {internalError || error || ""}
      </div>
    </div>
  );
};

export default CustomInput;
